import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { Button, Dialog, Field, Textarea } from '../../components/ui.jsx';
import { toast } from '../../lib/toast.js';

/**
 * Hiding an account from real users, or showing it again.
 *
 * For accounts made to test on production. The setting lives on the account,
 * so an agency is hidden through the account behind it — but the admin asks
 * from wherever they are looking at it, the agency page or the account page,
 * and gets the same dialog, wording and audit trail either way.
 *
 * Hiding asks for a reason, because it is the one people will later ask about;
 * showing again only undoes it.
 */
export function useAccountVisibility() {
  const queryClient = useQueryClient();

  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [isWorking, setIsWorking] = useState(false);

  const close = () => {
    setTarget(null);
    setReason('');
  };

  const hiding = Boolean(target && !target.hidden);
  const name = target?.name || 'this account';

  const submit = async () => {
    setIsWorking(true);
    try {
      await api.users.setVisibility(target.userId, hiding, reason.trim() || undefined);
      const who = target.name || 'That account';
      toast.success(hiding ? `${who} is now hidden from real users.` : `${who} is visible to everyone again.`);
      close();
      // Every list and page that shows the account or its agency.
      queryClient.invalidateQueries({ queryKey: ['admin-user'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-broker'] });
      queryClient.invalidateQueries({ queryKey: ['admin-brokers'] });
      queryClient.invalidateQueries({ queryKey: ['admin-brokers-unfinished'] });
      queryClient.invalidateQueries({ queryKey: ['admin-brokers-unfinished-count'] });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsWorking(false);
    }
  };

  const dialog = (
    <Dialog
      open={Boolean(target)}
      onClose={close}
      title={hiding ? `Hide ${name} from real users?` : `Show ${name} to real users?`}
      warning={
        hiding
          ? 'For test accounts. From now on no real user sees this account or anything it has posted — listings, requirements, its agency, its reviews — and it sees only other hidden accounts. Leads, enquiries, chats, visits and deals can no longer pass between it and real users; ones that already exist are left as they are. They can still sign in and use the app. This is reversible, and it is recorded in the audit log against your account.'
          : 'This account and everything it has posted become visible to real users again, and it sees theirs. This is recorded in the audit log against your account.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant={hiding ? 'danger' : 'primary'}
            onClick={submit}
            loading={isWorking}
            disabled={hiding && reason.trim().length < 3}
          >
            {hiding ? 'Hide account' : 'Show account'}
          </Button>
        </>
      }
    >
      {hiding ? (
        <Field label="Reason" hint="Recorded in the audit log against your account." id="visibility-reason">
          <Textarea
            id="visibility-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why is this account being hidden? e.g. QA account for testing on production"
          />
        </Field>
      ) : null}
    </Dialog>
  );

  return {
    /** `{ userId, name, hidden }` — the account, and whether it is hidden now. */
    ask: setTarget,
    dialog,
  };
}
